import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { UserPlus, Check, X, Mountain } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export default function FollowRequests() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/follow-requests");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const respond = async (req, action) => {
        try {
            await api.post(`/follow-requests/${req.id}/respond`, { action });
            toast.success(`Request ${action === "approve" ? "approved" : "rejected"}`);
            setItems((xs) => xs.filter((x) => x.id !== req.id));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="follow-requests-page">
            <TopBar title="Follow requests" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="mb-6">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">Approval inbox</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl text-midnight-pine tracking-tighter">Follow requests</h1>
                    <p className="text-slate-gray mt-2 text-sm">
                        These adventurers want to follow you. Approve to give them full access to your profile.
                    </p>
                </div>

                {loading ? (
                    <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-20 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />)}</div>
                ) : items.length === 0 ? (
                    <div className="bg-snowcap border border-ridge-line rounded-lg p-10 text-center" data-testid="follow-requests-empty">
                        <UserPlus className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                        <p className="text-sm text-slate-gray">No pending follow requests.</p>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {items.map((req) => (
                            <li
                                key={req.id}
                                data-testid={`follow-request-${req.id}`}
                                className="bg-snowcap border border-ridge-line rounded-lg p-4 flex items-center gap-3"
                            >
                                <Link to={`/profile/${req.requester.user_id}`} className="w-12 h-12 rounded-full overflow-hidden bg-trail-tan shrink-0 flex items-center justify-center">
                                    {req.requester.avatar
                                        ? <img src={req.requester.avatar} alt="" className="w-full h-full object-cover" />
                                        : <Mountain className="w-5 h-5 text-midnight-pine" />}
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <Link to={`/profile/${req.requester.user_id}`} className="font-bold text-midnight-pine hover:text-ember-orange transition-colors">
                                        {req.requester.name}
                                    </Link>
                                    {req.requester.username && (
                                        <div className="text-xs text-slate-gray">@{req.requester.username}</div>
                                    )}
                                    {req.requester.bio && (
                                        <p className="text-xs text-granite line-clamp-1 mt-0.5">{req.requester.bio}</p>
                                    )}
                                    <div className="text-[10px] text-slate-gray mt-0.5">
                                        Requested {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        data-testid={`follow-request-approve-${req.id}`}
                                        onClick={() => respond(req, "approve")}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark transition-all"
                                    >
                                        <Check className="w-3.5 h-3.5" /> Approve
                                    </button>
                                    <button
                                        data-testid={`follow-request-reject-${req.id}`}
                                        onClick={() => respond(req, "reject")}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-ridge-line text-granite text-xs font-bold hover:bg-alpine-mist transition-all"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
